// v77.99.35 — MoodPicker
//
// A 5-point mood scale + curated tag chips for the Journal new-entry
// dialog. Optional throughout: a user who doesn't want to log a mood
// can simply leave it unset and the entry still saves. Tag chips are
// multi-select; the curated list keeps tags consistent enough to be
// useful for the weekly summary aggregations.

import { Smile, Meh, Frown, Laugh, Angry } from "lucide-react";

const MOODS = [
  { value: 1, label: "Very low", Icon: Angry, color: "#8C2A22", bg: "#FBEFED" },
  { value: 2, label: "Low", Icon: Frown, color: "#883417", bg: "#FFE5D9" },
  { value: 3, label: "Neutral", Icon: Meh, color: "#4A6170", bg: "#EEF3F4" },
  { value: 4, label: "Good", Icon: Smile, color: "#0F5658", bg: "#E0F2F2" },
  { value: 5, label: "Very good", Icon: Laugh, color: "#116B6E", bg: "#D6F0EF" },
];

const TAG_OPTIONS = [
  "calm", "anxious", "frustrated", "hopeful",
  "tired", "supported", "conflicted", "happy",
];

export function MoodPicker({ value, onChange, tags = [], onTagsChange }) {
  const toggleTag = (t) => {
    if (!onTagsChange) return;
    onTagsChange(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);
  };
  return (
    <div className="space-y-2" data-testid="journal-mood-picker">
      <div className="text-sm font-medium text-[#0B2E32]">How are you feeling? <span className="text-xs text-[#6F828A] font-normal">(optional)</span></div>
      <div className="flex flex-wrap gap-2">
        {MOODS.map(({ value: v, label, Icon, color, bg }) => {
          const active = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange?.(active ? null : v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs transition-colors ${active ? "border-current" : "border-[#D9E1E3] hover:border-current/40"}`}
              style={{ color, background: active ? bg : "white" }}
              aria-pressed={active}
              data-testid={`journal-mood-${v}`}
              title={label}
            >
              <Icon className="w-4 h-4" strokeWidth={1.7} />
              <span className="font-medium">{label}</span>
            </button>
          );
        })}
        {value != null && (
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="text-xs text-[#6F828A] underline px-1"
            data-testid="journal-mood-clear"
          >
            Clear
          </button>
        )}
      </div>
      <div className="text-xs text-[#6F828A]">Tags <span className="text-[10px]">(optional, multi-select)</span></div>
      <div className="flex flex-wrap gap-1.5">
        {TAG_OPTIONS.map((t) => {
          const active = tags.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggleTag(t)}
              className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap capitalize ${active ? "bg-[#0B2E32] text-white border-[#0B2E32]" : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#F4FAFA]"}`}
              data-testid={`journal-mood-tag-${t}`}
            >
              {t}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const MOOD_META = MOODS;
export const MOOD_TAG_OPTIONS = TAG_OPTIONS;
